import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { buildFAQPageJsonLd } from './SEO';

/**
 * FAQ — collapsible Q&A section with SEO-friendly structure.
 * Includes automatic FAQPage JSON-LD injection (Google rich results).
 * Props:
 *  - items: Array<{ question: string, answer: string | React.ReactNode }>
 *  - heading (optional): custom heading
 */
const FAQ = ({ items, heading = 'Questions fréquentes' }) => {
  const [openIndex, setOpenIndex] = useState(null);

  if (!items || items.length === 0) return null;

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  // Build JSON-LD only with string-answers (skip React node answers for safety)
  const faqsForJsonLd = items
    .filter((it) => typeof it.answer === 'string')
    .map((it) => ({ question: it.question, answer: it.answer }));
  const faqJsonLd = faqsForJsonLd.length > 0 ? buildFAQPageJsonLd(faqsForJsonLd) : null;

  return (
    <section
      data-testid="faq-section"
      style={{
        background: 'white',
        padding: '4rem 0'
      }}
    >
      {faqJsonLd && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(faqJsonLd)}
          </script>
        </Helmet>
      )}
      <div className="container" style={{ maxWidth: '860px' }}>
        <h2
          className="heading-2"
          style={{ marginBottom: '2.5rem', textAlign: 'center' }}
        >
          {heading}
        </h2>

        <div
          itemScope
          itemType="https://schema.org/FAQPage"
          style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
        >
          {items.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                data-testid={`faq-item-${i}`}
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                style={{
                  background: 'var(--bg-page)',
                  borderRadius: '0.875rem',
                  border: '1px solid var(--border-light)',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease'
                }}
              >
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  aria-expanded={isOpen}
                  data-testid={`faq-question-${i}`}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    padding: '1.25rem 1.5rem',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '1.05rem',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    lineHeight: 1.5
                  }}
                >
                  <span itemProp="name">{item.question}</span>
                  <ChevronDown
                    size={20}
                    style={{
                      flexShrink: 0,
                      color: 'var(--accent-primary)',
                      transition: 'transform 0.25s ease',
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0)'
                    }}
                  />
                </button>
                {isOpen && (
                  <div
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                    style={{
                      padding: '0 1.5rem 1.5rem',
                      color: 'var(--text-secondary)',
                      lineHeight: 1.75,
                      fontSize: '1rem'
                    }}
                  >
                    <div itemProp="text" data-testid={`faq-answer-${i}`}>
                      {typeof item.answer === 'string' ? (
                        <p style={{ margin: 0 }}>{item.answer}</p>
                      ) : (
                        item.answer
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
